import { Col, Row } from 'antd';
import { useFormikContext } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { type ModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/ModuleWithPermissionsSpecs';
import { MobilityKind, ModuleRole } from '../../../../api/types';
import ModuleSelectField from '../../../../components/fields/ModuleSelectField';
import TransferField from '../../../../components/fields/TransferField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';
import AdditionalInfoDetailFieldArray from './AdditionalInfoDetailFieldArray';
import AddOnOptionsFieldArray from './AddOnOptionFieldArray';
import { type CreateMobilityFormValues } from './types';
import useMobilityStockOptions from './useMobilityStockOptions';
import useMobilityTypeOptions from './useMobilityTypeOptions';

const colSpan = { lg: 8, md: 12, xs: 24 };

type FormFieldsProps = {
    isUpdate: boolean;
    updated?: string;
    offset?: string;
    disabled?: boolean;
    module?: ModuleWithPermissionsSpecsFragment;
};
const MobilityFormFields = ({ isUpdate, updated, offset, module, disabled = false }: FormFieldsProps) => {
    const { t } = useTranslation(['mobilityDetails']);

    const { initialValues, values, setFieldValue } = useFormikContext<CreateMobilityFormValues>();

    const { yesNoSwitch } = useSystemSwitchData();
    const mobilityTypeOptions = useMobilityTypeOptions();

    const { moduleId } = values;

    const stockOptions = useMobilityStockOptions(moduleId);

    // reset stock ids when module changes for creation
    useEffect(() => {
        if (!isUpdate && !!moduleId) {
            setFieldValue('stockIds', []);
        }
    }, [moduleId, isUpdate, setFieldValue]);

    return (
        <CollapsibleWrapper defaultActiveKey={t('mobilityDetails:cardTitles.mobilityDetails')}>
            <Panel
                key={t('mobilityDetails:cardTitles.mobilityDetails')}
                header={t('mobilityDetails:cardTitles.mobilityDetails')}
            >
                <Row gutter={14}>
                    <Col {...colSpan}>
                        <FormFields.SelectField
                            {...t('mobilityDetails:fields.type', { returnObjects: true })}
                            disabled={disabled}
                            name="kind"
                            options={mobilityTypeOptions}
                            required
                            showSearch
                        />
                    </Col>
                    <Col {...colSpan}>
                        {isUpdate ? (
                            <FormFields.DisplayField
                                {...t('mobilityDetails:fields.module', { returnObjects: true })}
                                value={module.displayName}
                            />
                        ) : (
                            <ModuleSelectField
                                {...t('mobilityDetails:fields.module', { returnObjects: true })}
                                disabled={isUpdate || disabled}
                                moduleRole={ModuleRole.Mobility}
                                name="moduleId"
                                required
                            />
                        )}
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.TranslatedInputField
                            {...t('mobilityDetails:fields.title', { returnObjects: true })}
                            disabled={disabled}
                            name="title"
                            required
                        />
                    </Col>
                    {values.kind === MobilityKind.Addon && (
                        <>
                            <Col {...colSpan}>
                                <FormFields.InputField
                                    {...t('mobilityDetails:fields.referenceName', { returnObjects: true })}
                                    disabled={disabled}
                                    name="referenceName"
                                    required
                                />
                            </Col>
                            <Col {...colSpan}>
                                <FormFields.SwitchField
                                    {...t('mobilityDetails:fields.chargeable', {
                                        returnObjects: true,
                                    })}
                                    {...yesNoSwitch}
                                    defaultChecked={false}
                                    disabled={disabled}
                                    name="isChargeable"
                                />
                            </Col>
                        </>
                    )}
                    <Col {...colSpan}>
                        <FormFields.InputNumberField
                            {...t('mobilityDetails:fields.order', {
                                returnObjects: true,
                            })}
                            disabled={disabled}
                            min={1}
                            name="order"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.SwitchField
                            {...t('mobilityDetails:fields.active', {
                                returnObjects: true,
                            })}
                            {...yesNoSwitch}
                            defaultChecked={initialValues.isActive}
                            disabled={disabled}
                            name="isActive"
                        />
                    </Col>
                    {updated && (
                        <Col {...colSpan}>
                            <FormFields.DisplayFieldWithUTCOffset
                                {...t('mobilityDetails:fields.updatedAt', { returnObjects: true, offset })}
                                value={updated}
                            />
                        </Col>
                    )}
                </Row>
                <Row gutter={14}>
                    <Col xs={24}>
                        <TransferField
                            dataSource={stockOptions}
                            disabled={disabled}
                            label={t('mobilityDetails:fields.stockIds.label')}
                            listStyle={{ width: '100%' }}
                            name="stockIds"
                            render={item => item?.label}
                            required
                        />
                    </Col>
                </Row>
                {values.kind === MobilityKind.Addon && (
                    <AddOnOptionsFieldArray disabled={disabled} isChargeable={values.isChargeable} />
                )}
                {values.kind === MobilityKind.AdditionalInfo && <AdditionalInfoDetailFieldArray disabled={disabled} />}
            </Panel>
        </CollapsibleWrapper>
    );
};

export default MobilityFormFields;
